import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, ArrowLeft, Upload, X, Bold, Italic, Heading2, Heading3, Link2, Plus, ImagePlus, List, ListOrdered, Minus, Table as TableIcon, Quote, Unlink, Youtube, Trash2, Music } from 'lucide-react';
import { rawFetch } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { useEditor, EditorContent } from '@tiptap/react';
import { BubbleMenu, FloatingMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import ImageResize from 'tiptap-extension-resize-image';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import { Typography } from '@tiptap/extension-typography';
import YoutubeExtension from '@tiptap/extension-youtube';
import { NodeSelection } from '@tiptap/pm/state';
import TableBubbleMenu from '../components/TableBubbleMenu';
import { buildTenantPath } from '../hooks/useTenant';

interface Membership {
  id: number;
  name: string;
}

interface Track {
  id: number;
  title: string;
  trackReference: string;
  verificationStatus: string;
}

interface VaultTrack {
  id: number;
  trackId: number;
  label: string | null;
  trackTitle: string | null;
  trackReference: string | null;
  coverS3Uri: string | null;
  coverUrl: string | null;
}

interface VaultData {
  id?: number;
  name: string | null;
  information: string | null;
  productionType: string | null;
  productionLimit: number | null;
  productionExpiryDate: number | null;
  memberships: number[];
  coverS3Uri: string | null;
  coverUrl: string | null;
  credits: number;
}

export default function VaultEditorPage({ slug, vaultId }: { slug: string; vaultId: string }) {
  const { token } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [currentVaultId, setCurrentVaultId] = useState(vaultId);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [vault, setVault] = useState<VaultData>({
    name: null,
    information: null,
    productionType: null,
    productionLimit: null,
    productionExpiryDate: null,
    memberships: [],
    coverS3Uri: null,
    coverUrl: null,
    credits: 0,
  });
  const vaultRef = useRef(vault);
  vaultRef.current = vault;

  const [vaultTracks, setVaultTracks] = useState<VaultTrack[]>([]);

  // Cover upload state
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  const [uploadingCover, setUploadingCover] = useState(false);

  // Track modal
  const [showTrackModal, setShowTrackModal] = useState(false);
  const [availableTracks, setAvailableTracks] = useState<Track[]>([]);
  const [trackLabel, setTrackLabel] = useState('');
  const [selectedTrackId, setSelectedTrackId] = useState<number | null>(null);

  const vaultIdRef = useRef(vaultId);

  // Link input state
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const linkInputRef = useRef<HTMLInputElement>(null);

  // YouTube modal
  const [showYoutubeModal, setShowYoutubeModal] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const youtubeInputRef = useRef<HTMLInputElement>(null);

  const isNew = currentVaultId === 'new';

  // Image upload helper
  const uploadContentImage = useCallback(async (file: File): Promise<string | null> => {
    try {
      const optimized = await optimizeImage(file);
      const presignRes = await rawFetch(`/api/public/tenants/vaults/content-image/presign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': slug, Authorization: `Bearer ${token}` },
        body: JSON.stringify({ contentType: 'image/webp', fileSize: optimized.size }),
      });
      if (!presignRes.ok) throw new Error('Presign failed');
      const presignJson = await presignRes.json() as { data: { uploadUrl: string; path: string } };
      const uploadRes = await fetch(presignJson.data.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'image/webp' },
        body: optimized,
      });
      if (!uploadRes.ok) throw new Error('Upload failed');
      const confirmRes = await rawFetch(`/api/public/tenants/vaults/content-image/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': slug, Authorization: `Bearer ${token}` },
        body: JSON.stringify({ path: presignJson.data.path }),
      });
      if (!confirmRes.ok) throw new Error('Confirm failed');
      const confirmJson = await confirmRes.json() as { data: { url: string } };
      return confirmJson.data.url;
    } catch (err) {
      console.error('Content image upload failed:', err);
      return null;
    }
  }, [slug, token]);

  // TipTap editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        blockquote: {},
        horizontalRule: {},
        bulletList: {},
        orderedList: {},
        link: false,
      }),
      Placeholder.configure({ placeholder: 'Write your liner notes here...' }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          class: 'text-electric-cyan underline underline-offset-2 hover:text-electric-cyan/80 transition-colors cursor-pointer',
        },
      }),
      ImageResize.configure({ inline: true, minWidth: 100, maxWidth: 800 }),
      Table.configure({
        resizable: true,
        HTMLAttributes: { class: 'border-collapse border border-white/10 w-full' },
      }),
      TableRow.configure({ HTMLAttributes: { class: 'border border-white/10' } }),
      TableCell.configure({ HTMLAttributes: { class: 'border border-white/10 p-2 text-gray-300' } }),
      TableHeader.configure({ HTMLAttributes: { class: 'border border-white/10 p-2 bg-white/5 text-white font-semibold' } }),
      Typography,
      YoutubeExtension.configure({
        nocookie: true, controls: true, allowFullscreen: true,
        width: 640, height: 360,
        HTMLAttributes: { class: 'rounded-lg overflow-hidden my-4' },
      }),
    ],
    content: null,
    onUpdate: () => {
      setIsDirty(true);
    },
    editorProps: {
      attributes: {
        class: 'outline-none min-h-[200px] prose prose-invert max-w-none prose-headings:text-white prose-p:text-gray-300 prose-a:text-electric-cyan prose-strong:text-white prose-img:rounded-lg prose-img:max-w-full prose-blockquote:border-electric-cyan/40 prose-blockquote:text-gray-400 prose-hr:border-white/10',
      },
      handleDrop: (view, event, _slice, moved) => {
        if (moved) return false;
        const files = event.dataTransfer?.files;
        if (!files || files.length === 0) return false;
        const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
        if (imageFiles.length === 0) return false;
        event.preventDefault();
        imageFiles.forEach(async (file) => {
          const url = await uploadContentImage(file);
          if (url && editor) editor.chain().focus().setImage({ src: url }).run();
        });
        return true;
      },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;
        const imageItem = Array.from(items).find(item => item.type.startsWith('image/'));
        if (!imageItem) return false;
        const file = imageItem.getAsFile();
        if (!file) return false;
        event.preventDefault();
        (async () => {
          const url = await uploadContentImage(file);
          if (url && editor) editor.chain().focus().setImage({ src: url }).run();
        })();
        return true;
      },
      handleKeyDown: (view, event) => {
        // Delete entire table when Backspace/Delete is pressed and table is empty
        if ((event.key === 'Backspace' || event.key === 'Delete') && editor?.isActive('table')) {
          const { state } = view;
          const { selection } = state;
          // Check if it's a NodeSelection on the table
          if (selection instanceof NodeSelection && selection.node.type.name === 'table') {
            editor.chain().focus().deleteTable().run();
            return true;
          }
          // Check if all cells in the table are empty
          const $pos = state.doc.resolve(selection.from);
          for (let depth = $pos.depth; depth > 0; depth--) {
            const node = $pos.node(depth);
            if (node.type.name === 'table') {
              let hasContent = false;
              node.descendants((child) => {
                if (child.isText && child.text && child.text.trim().length > 0) {
                  hasContent = true;
                }
              });
              if (!hasContent) {
                editor.chain().focus().deleteTable().run();
                return true;
              }
              break;
            }
          }
        }
        return false;
      },
    },
  });

  // Image upload input ref
  const imageInputRef = useRef<HTMLInputElement>(null);
  const handleImageSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;
    e.target.value = '';
    const url = await uploadContentImage(file);
    if (url) editor.chain().focus().setImage({ src: url }).run();
  }, [editor, uploadContentImage]);

  // Link helpers
  const openLinkInput = useCallback(() => {
    if (!editor) return;
    const existingUrl = editor.getAttributes('link').href || '';
    setLinkUrl(existingUrl);
    setShowLinkInput(true);
    setTimeout(() => linkInputRef.current?.focus(), 50);
  }, [editor]);

  const applyLink = useCallback(() => {
    if (!editor) return;
    if (linkUrl === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: linkUrl }).run();
    }
    setShowLinkInput(false);
    setLinkUrl('');
  }, [editor, linkUrl]);

  const removeLink = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    setShowLinkInput(false);
    setLinkUrl('');
  }, [editor]);

  // YouTube embed helper
  const openYoutubeModal = useCallback(() => {
    setYoutubeUrl('');
    setShowYoutubeModal(true);
    setTimeout(() => youtubeInputRef.current?.focus(), 50);
  }, []);

  const embedYoutube = useCallback(() => {
    if (!editor || !youtubeUrl.trim()) return;
    editor.commands.setYoutubeVideo({ src: youtubeUrl.trim() });
    setShowYoutubeModal(false);
    setYoutubeUrl('');
  }, [editor, youtubeUrl]);

  useEffect(() => {
    const init = async () => {
      const fetchedMemberships = await fetchMemberships();
      if (!isNew) {
        await fetchVault();
      } else {
        // For new vaults, enable all memberships by default
        if (fetchedMemberships && fetchedMemberships.length > 0) {
          setVault(prev => ({ ...prev, memberships: fetchedMemberships.map(m => m.id) }));
        }
        setLoading(false);
      }
    };
    init();
  }, []);

  const fetchMemberships = async (): Promise<Membership[]> => {
    try {
      const res = await rawFetch(`/api/public/tenants/memberships`, {
        headers: { 'X-Tenant-Slug': slug, Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const json = await res.json() as { data: Membership[] };
        setMemberships(json.data);
        return json.data;
      }
    } catch {}
    return [];
  };

  const fetchVault = async () => {
    try {
      const res = await rawFetch(`/api/public/tenants/vaults/${vaultId}`, {
        headers: { 'X-Tenant-Slug': slug, Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const json = await res.json() as { data: any };
        const d = json.data;
        let membershipIds: number[] = [];
        if (d.memberships) {
          try { membershipIds = JSON.parse(d.memberships); } catch {}
        }
        setVault({
          id: d.id,
          name: d.name,
          information: d.information,
          productionType: d.productionType,
          productionLimit: d.productionLimit,
          productionExpiryDate: d.productionExpiryDate,
          memberships: membershipIds,
          coverS3Uri: d.coverS3Uri,
          coverUrl: d.coverUrl,
          credits: d.credits,
        });
        setCoverUrl(d.coverUrl);
        setVaultTracks(d.tracks || []);
        if (d.information && editor) {
          try {
            const parsed = JSON.parse(d.information);
            editor.commands.setContent(parsed);
          } catch {}
        }
        setIsDirty(false);
        setSaveError(null);
        setLoading(false);
      }
    } catch (err) {
      console.error('Failed to fetch vault:', err);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (editor && vault.information && !isNew) {
      try {
        const parsed = JSON.parse(vault.information);
        editor.commands.setContent(parsed);
      } catch {}
    }
  }, [editor, vault.information]);

  const optimizeImage = async (file: File): Promise<Blob> => {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(img, 0, 0);
        canvas.toBlob((blob) => resolve(blob || file), 'image/webp', 0.82);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handleCoverUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) return;
    setUploadingCover(true);
    try {
      let currentId = vaultId;
      if (isNew) {
        const createRes = await rawFetch(`/api/public/tenants/vaults`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': slug, Authorization: `Bearer ${token}` },
          body: JSON.stringify({}),
        });
        if (createRes.ok) {
          const createJson = await createRes.json() as { data: any };
          currentId = String(createJson.data.id);
          vaultIdRef.current = currentId;
          setCurrentVaultId(currentId);
          window.history.replaceState(null, '', buildTenantPath(slug, `pressings/${currentId}`));
        } else throw new Error('Failed to create vault');
      }
      const optimizedFile = await optimizeImage(file);
      const presignRes = await rawFetch(`/api/public/tenants/vaults/${currentId}/cover/presign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': slug, Authorization: `Bearer ${token}` },
        body: JSON.stringify({ contentType: 'image/webp', fileSize: optimizedFile.size }),
      });
      if (!presignRes.ok) throw new Error('Failed to get upload URL');
      const presignJson = await presignRes.json() as { data: { uploadUrl: string; path: string } };
      await fetch(presignJson.data.uploadUrl, { method: 'PUT', headers: { 'Content-Type': 'image/webp' }, body: optimizedFile });
      const confirmRes = await rawFetch(`/api/public/tenants/vaults/${currentId}/cover/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': slug, Authorization: `Bearer ${token}` },
        body: JSON.stringify({ path: presignJson.data.path }),
      });
      if (confirmRes.ok) {
        const confirmJson = await confirmRes.json() as { data: { coverUrl: string; coverS3Uri: string } };
        setCoverUrl(confirmJson.data.coverUrl);
        setVault(prev => ({ ...prev, coverS3Uri: confirmJson.data.coverS3Uri, coverUrl: confirmJson.data.coverUrl }));
        setIsDirty(true);
      }
    } catch (err) {
      console.error('Cover upload failed:', err);
    } finally {
      setUploadingCover(false);
    }
  };

  // Track management
  const fetchAvailableTracks = async () => {
    try {
      const res = await rawFetch(`/api/public/tenants/tracks`, {
        headers: { 'X-Tenant-Slug': slug, Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const json = await res.json() as { data: any[] };
        const verified = json.data
          .filter((t: any) => t.verificationStatus === 'VERIFIED')
          .map((t: any) => ({
            id: typeof t.id === 'string' ? parseInt(t.id, 10) : t.id,
            title: t.title,
            trackReference: t.trackReference,
            verificationStatus: t.verificationStatus,
          }))
          .filter((t: Track) => Number.isFinite(t.id));
        setAvailableTracks(verified);
      }
    } catch {}
  };

  const openTrackModal = () => {
    setTrackLabel('');
    setSelectedTrackId(null);
    fetchAvailableTracks();
    setShowTrackModal(true);
  };

  const addTrack = async () => {
    if (!selectedTrackId) return;
    const currentId = vaultIdRef.current;
    if (currentId === 'new') return;
    try {
      const res = await rawFetch(`/api/public/tenants/vaults/${currentId}/tracks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': slug, Authorization: `Bearer ${token}` },
        body: JSON.stringify({ trackId: selectedTrackId, label: trackLabel || null }),
      });
      if (res.ok) {
        const json = await res.json() as { data: { id: number; trackId: number; label: string | null } };
        // Optimistically add the new track to the local list
        const selectedTrack = availableTracks.find(t => t.id === selectedTrackId);
        setVaultTracks(prev => [...prev, {
          id: json.data.id,
          trackId: json.data.trackId,
          label: json.data.label,
          trackTitle: selectedTrack?.title || null,
          trackReference: selectedTrack?.trackReference || null,
          coverS3Uri: null,
          coverUrl: null,
        }]);
        setShowTrackModal(false);
      }
    } catch {}
  };

  const removeTrack = async (vaultTrackId: number) => {
    const currentId = vaultIdRef.current;
    try {
      await rawFetch(`/api/public/tenants/vaults/${currentId}/tracks/${vaultTrackId}`, {
        method: 'DELETE',
        headers: { 'X-Tenant-Slug': slug, Authorization: `Bearer ${token}` },
      });
      setVaultTracks(prev => prev.filter(t => t.id !== vaultTrackId));
    } catch {}
  };

  const handleFieldChange = (field: keyof VaultData, value: any) => {
    setVault(prev => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const handleSave = async () => {
    const trimmedName = (vaultRef.current.name || '').trim();
    if (!trimmedName) {
      setSaveStatus('error');
      setSaveError('Pressing title is required before saving.');
      return;
    }

    setSaveStatus('saving');
    setSaveError(null);

    try {
      const payload: any = {
        name: trimmedName,
        information: editor ? JSON.stringify(editor.getJSON()) : vaultRef.current.information,
        productionType: vaultRef.current.productionType,
        productionLimit: vaultRef.current.productionLimit,
        productionExpiryDate: vaultRef.current.productionExpiryDate,
        memberships: vaultRef.current.memberships,
        credits: vaultRef.current.credits,
        coverS3Uri: vaultRef.current.coverS3Uri,
      };

      let targetVaultId = vaultIdRef.current;
      if (targetVaultId === 'new') {
        const createRes = await rawFetch('/api/public/tenants/vaults', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': slug, Authorization: `Bearer ${token}` },
          body: JSON.stringify({}),
        });

        const createJson = await createRes.json().catch(() => ({} as any));
        if (!createRes.ok || !createJson?.data?.id) {
          const reason = createJson?.error || 'Failed to create pressing';
          setSaveStatus('error');
          setSaveError(String(reason));
          return;
        }

        targetVaultId = String(createJson.data.id);
        vaultIdRef.current = targetVaultId;
        setCurrentVaultId(targetVaultId);
        window.history.replaceState(null, '', buildTenantPath(slug, `pressings/${targetVaultId}`));
      }

      const res = await rawFetch(`/api/public/tenants/vaults/${targetVaultId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': slug, Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });

      const json = await res.json().catch(() => ({} as any));
      if (!res.ok) {
        const reason = json?.details?.reason || json?.details || json?.error || 'Failed to save pressing';
        setSaveStatus('error');
        setSaveError(String(reason));
        return;
      }

      setSaveStatus('saved');
      setIsDirty(false);
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err: any) {
      setSaveStatus('error');
      setSaveError(err?.message || 'Failed to save pressing');
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this pressing?')) return;
    try {
      await rawFetch(`/api/public/tenants/vaults/${vaultIdRef.current}`, {
        method: 'DELETE',
        headers: { 'X-Tenant-Slug': slug, Authorization: `Bearer ${token}` },
      });
      window.location.href = buildTenantPath(slug, 'pressings');
    } catch {}
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-deep-charcoal flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-electric-cyan animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-charcoal">
      {/* Top bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-card border-b border-white/5 rounded-none">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href={buildTenantPath(slug, 'pressings')} className="flex items-center gap-1.5 text-text-secondary hover:text-electric-cyan transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-xs font-mono uppercase tracking-wider">Pressings</span>
            </a>
          </div>
          <div className="flex items-center gap-3">
            {saveStatus === 'saving' && (
              <span className="text-xs text-text-muted font-mono flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Saving...
              </span>
            )}
            {saveStatus === 'saved' && (
              <span className="text-xs text-green-400 font-mono">Saved</span>
            )}
            {saveStatus === 'error' && saveError && (
              <span className="text-xs text-red-400 font-mono max-w-[16rem] truncate" title={saveError}>Save failed</span>
            )}
            <button
              onClick={handleSave}
              disabled={saveStatus === 'saving' || (!isDirty && !isNew)}
              className="btn-primary text-xs !px-3 !py-1.5 disabled:opacity-50"
            >
              {isNew ? 'Create Pressing' : 'Save'}
            </button>
            {!isNew && (
              <button onClick={handleDelete} className="text-red-400 hover:text-red-300 transition-colors p-1.5">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </nav>

      <main className="pt-14 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {saveError && (
          <div className="mb-4 rounded border border-red-400/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
            {saveError}
          </div>
        )}

        {/* Name */}
        <input
          type="text"
          value={vault.name || ''}
          onChange={(e) => handleFieldChange('name', e.target.value || null)}
          placeholder="Pressing title (required)"
          className="w-full bg-transparent text-3xl font-bold text-white placeholder-text-muted outline-none border-none mb-6"
        />

        {/* Cover Art */}
        <div className="mb-6">
          <label className="block text-xs font-mono uppercase tracking-wider text-text-secondary mb-2">Cover Art (Square)</label>
          {coverUrl ? (
            <div className="relative w-48 h-48 group">
              <img src={coverUrl} alt="Cover" className="w-full h-full object-cover rounded-lg border border-white/10" />
              <button
                onClick={() => { setCoverUrl(null); setVault(prev => ({ ...prev, coverS3Uri: null, coverUrl: null })); setIsDirty(true); }}
                className="absolute top-2 right-2 p-1 bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          ) : (
            <label className="w-48 h-48 flex flex-col items-center justify-center border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-electric-cyan/40 transition-colors">
              {uploadingCover ? <Loader2 className="w-6 h-6 text-electric-cyan animate-spin" /> : (
                <>
                  <Upload className="w-6 h-6 text-text-muted mb-2" />
                  <span className="text-xs text-text-muted">Upload Cover</span>
                </>
              )}
              <input type="file" className="hidden" accept="image/*" onChange={(e) => { if (e.target.files?.[0]) handleCoverUpload(e.target.files[0]); }} />
            </label>
          )}
        </div>

        {/* Memberships */}
        <div className="mb-6">
          <label className="block text-xs font-mono uppercase tracking-wider text-text-secondary mb-2">Memberships</label>
          <div className="flex flex-wrap gap-2">
            {memberships.map(m => {
              const isActive = vault.memberships.includes(m.id);
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => {
                    const next = isActive
                      ? vault.memberships.filter(id => id !== m.id)
                      : [...vault.memberships, m.id];
                    handleFieldChange('memberships', next);
                  }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer border ${
                    isActive
                      ? 'bg-electric-cyan/20 border-electric-cyan/50 text-electric-cyan'
                      : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/20'
                  }`}
                >
                  {m.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Production Type */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-text-secondary mb-2">Production Type</label>
            <select
              value={vault.productionType || ''}
              onChange={(e) => handleFieldChange('productionType', e.target.value || null)}
              className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm text-white focus:border-electric-cyan/50 focus:outline-none"
            >
              <option value="">Select...</option>
              <option value="Unlimited Edition">Unlimited Edition</option>
              <option value="Timed Edition">Timed Edition</option>
              <option value="Limited Edition">Limited Edition</option>
            </select>
          </div>
          {vault.productionType === 'Limited Edition' && (
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-text-secondary mb-2">Production Limit</label>
              <input
                type="number"
                value={vault.productionLimit || ''}
                onChange={(e) => handleFieldChange('productionLimit', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm text-white focus:border-electric-cyan/50 focus:outline-none"
                placeholder="e.g. 500"
                min="1"
              />
            </div>
          )}
          {vault.productionType === 'Timed Edition' && (
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-text-secondary mb-2">Expiry Date</label>
              <input
                type="date"
                value={vault.productionExpiryDate ? new Date(vault.productionExpiryDate * 1000).toISOString().split('T')[0] : ''}
                onChange={(e) => handleFieldChange('productionExpiryDate', e.target.value ? Math.floor(new Date(e.target.value).getTime() / 1000) : null)}
                className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm text-white focus:border-electric-cyan/50 focus:outline-none"
              />
            </div>
          )}
        </div>

        {/* Information (TipTap Editor) */}
        <div className="mb-6">
          <label className="block text-xs font-mono uppercase tracking-wider text-text-secondary mb-2">Information (Liner Notes)</label>
          <div className="glass-card border border-white/10 rounded-lg p-4">
            {editor && (
              <>
                <BubbleMenu editor={editor} pluginKey="textFormatMenu" shouldShow={({ editor, state }) => { const { from, to } = state.selection; if (from === to) return false; if (editor.isActive('table')) return false; return true; }} options={{ placement: 'top', offset: 8 }}>
                  <div className="flex items-center gap-0.5 bg-[#1A1A1A] border border-white/10 rounded-lg p-1 shadow-xl">
                    <button onClick={() => editor.chain().focus().toggleBold().run()} className={`p-1.5 rounded ${editor.isActive('bold') ? 'bg-electric-cyan/20 text-electric-cyan' : 'text-gray-400 hover:text-white'}`}><Bold className="w-4 h-4" /></button>
                    <button onClick={() => editor.chain().focus().toggleItalic().run()} className={`p-1.5 rounded ${editor.isActive('italic') ? 'bg-electric-cyan/20 text-electric-cyan' : 'text-gray-400 hover:text-white'}`}><Italic className="w-4 h-4" /></button>
                    <button onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={`p-1.5 rounded ${editor.isActive('heading', { level: 2 }) ? 'bg-electric-cyan/20 text-electric-cyan' : 'text-gray-400 hover:text-white'}`}><Heading2 className="w-4 h-4" /></button>
                    <button onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={`p-1.5 rounded ${editor.isActive('heading', { level: 3 }) ? 'bg-electric-cyan/20 text-electric-cyan' : 'text-gray-400 hover:text-white'}`}><Heading3 className="w-4 h-4" /></button>
                    {!showLinkInput ? (
                      <button onClick={openLinkInput} className={`p-1.5 rounded ${editor.isActive('link') ? 'bg-electric-cyan/20 text-electric-cyan' : 'text-gray-400 hover:text-white'}`}><Link2 className="w-4 h-4" /></button>
                    ) : (
                      <div className="flex items-center gap-1 px-1">
                        <input ref={linkInputRef} type="url" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') applyLink(); if (e.key === 'Escape') setShowLinkInput(false); }} placeholder="URL" className="w-32 bg-white/5 border border-white/10 rounded px-2 py-0.5 text-xs text-white" />
                        <button onClick={applyLink} className="p-1 text-electric-cyan"><Link2 className="w-3 h-3" /></button>
                        {editor.isActive('link') && <button onClick={removeLink} className="p-1 text-red-400"><Unlink className="w-3 h-3" /></button>}
                      </div>
                    )}
                    <button onClick={() => editor.chain().focus().toggleBlockquote().run()} className={`p-1.5 rounded ${editor.isActive('blockquote') ? 'bg-electric-cyan/20 text-electric-cyan' : 'text-gray-400 hover:text-white'}`}><Quote className="w-4 h-4" /></button>
                  </div>
                </BubbleMenu>
                <FloatingMenu editor={editor} options={{ placement: 'top', offset: 8 }}>
                  <div className="flex items-center gap-0.5 bg-[#1A1A1A] border border-white/10 rounded-lg p-1 shadow-xl">
                    <button onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className="p-1.5 rounded text-gray-400 hover:text-white"><Heading2 className="w-4 h-4" /></button>
                    <button onClick={() => editor.chain().focus().toggleBulletList().run()} className="p-1.5 rounded text-gray-400 hover:text-white"><List className="w-4 h-4" /></button>
                    <button onClick={() => editor.chain().focus().toggleOrderedList().run()} className="p-1.5 rounded text-gray-400 hover:text-white"><ListOrdered className="w-4 h-4" /></button>
                    <button onClick={() => editor.chain().focus().setHorizontalRule().run()} className="p-1.5 rounded text-gray-400 hover:text-white"><Minus className="w-4 h-4" /></button>
                    <button onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3 }).run()} className="p-1.5 rounded text-gray-400 hover:text-white"><TableIcon className="w-4 h-4" /></button>
                    <button onClick={() => imageInputRef.current?.click()} className="p-1.5 rounded text-gray-400 hover:text-white"><ImagePlus className="w-4 h-4" /></button>
                    <button onClick={openYoutubeModal} className="p-1.5 rounded text-gray-400 hover:text-white"><Youtube className="w-4 h-4" /></button>
                  </div>
                </FloatingMenu>
              </>
            )}
            <EditorContent editor={editor} />

            {/* Table Bubble Menu - appears when cursor is in a table */}
            {editor && <TableBubbleMenu editor={editor} />}
            <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageSelect} />
          </div>
        </div>

        {/* Tracks */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-xs font-mono uppercase tracking-wider text-text-secondary">Tracks</label>
            {!isNew && <button onClick={openTrackModal} className="text-xs text-electric-cyan hover:text-electric-cyan/80 flex items-center gap-1"><Plus className="w-3 h-3" />Add Track</button>}
          </div>
          {vaultTracks.length === 0 ? (
            <div className="glass-card p-6 text-center border border-white/5">
              <Music className="w-8 h-8 text-text-muted mx-auto mb-2" />
              <p className="text-xs text-text-muted">No tracks added yet</p>
            </div>
          ) : (
            <div className="glass-card border border-white/5 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left px-4 py-2 font-mono text-xs uppercase text-text-secondary w-10"></th>
                    <th className="text-left px-4 py-2 font-mono text-xs uppercase text-text-secondary">Track</th>
                    <th className="text-left px-4 py-2 font-mono text-xs uppercase text-text-secondary">Label</th>
                    <th className="text-right px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {vaultTracks.map(t => (
                    <tr key={t.id} className="border-b border-white/5">
                      <td className="px-4 py-2">
                        {t.coverUrl ? (
                          <img src={t.coverUrl} alt={t.trackTitle || 'Cover'} className="w-8 h-8 rounded object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded bg-white/5 flex items-center justify-center">
                            <Music className="w-4 h-4 text-text-muted" />
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-white">{t.trackTitle || '—'}</td>
                      <td className="px-4 py-2 text-text-secondary text-xs">{t.label || '—'}</td>
                      <td className="px-4 py-2 text-right">
                        <button onClick={() => removeTrack(t.id)} className="text-red-400 hover:text-red-300 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </main>

      {/* Track Selection Modal */}
      {showTrackModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="glass-card p-6 w-full max-w-md mx-4 border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Add Track</h3>
              <button onClick={() => setShowTrackModal(false)} className="text-text-muted hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="mb-4">
              <label className="block text-xs font-mono uppercase tracking-wider text-text-secondary mb-1">Label (optional)</label>
              <input type="text" value={trackLabel} onChange={(e) => setTrackLabel(e.target.value)} placeholder="e.g. Early Demo" className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm text-white focus:border-electric-cyan/50 focus:outline-none" />
            </div>
            <div className="mb-4">
              <label className="block text-xs font-mono uppercase tracking-wider text-text-secondary mb-1">Select Track (Verified only)</label>
              <select value={selectedTrackId || ''} onChange={(e) => setSelectedTrackId(e.target.value ? parseInt(e.target.value) : null)} className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm text-white focus:border-electric-cyan/50 focus:outline-none">
                <option value="">Choose a track...</option>
                {availableTracks.map(t => (
                  <option key={t.id} value={t.id}>{t.title}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowTrackModal(false)} className="px-4 py-2 text-sm text-text-secondary hover:text-white transition-colors">Cancel</button>
              <button onClick={addTrack} disabled={!selectedTrackId} className="btn-primary text-sm !px-4 !py-2 disabled:opacity-50">Add</button>
            </div>
          </div>
        </div>
      )}

      {/* YouTube Modal */}
      {showYoutubeModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="glass-card p-6 w-full max-w-md mx-4 border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Embed YouTube</h3>
              <button onClick={() => setShowYoutubeModal(false)} className="text-text-muted hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <input ref={youtubeInputRef} type="url" value={youtubeUrl} onChange={(e) => setYoutubeUrl(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') embedYoutube(); }} placeholder="https://youtube.com/watch?v=..." className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm text-white focus:border-electric-cyan/50 focus:outline-none mb-4" />
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowYoutubeModal(false)} className="px-4 py-2 text-sm text-text-secondary hover:text-white transition-colors">Cancel</button>
              <button onClick={embedYoutube} disabled={!youtubeUrl.trim()} className="btn-primary text-sm !px-4 !py-2 disabled:opacity-50">Embed</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
